import React, { useEffect, useState } from "react";
import { CalendarDays, Stethoscope, Home, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill } from "../../components/Shared";

export default function CalendarPage() {
  const [events, setEvents] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [parents, setParents] = useState([]);
  const [form, setForm] = useState({ title: "", when: "", location: "", notes: "", parent_id: "" });
  const load = () => api.get("/family/calendar").then((r) => setEvents(r.data));
  useEffect(() => { load().catch(() => setEvents([])); api.get("/family/parents").then((r) => setParents(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family/appointments", { ...form, when: new Date(form.when).toISOString() });
      toast.success("Appointment added"); setShowForm(false); setForm({ title: "", when: "", location: "", notes: "" }); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  if (!events) return <Loading />;
  const upcoming = events.filter((e) => new Date(e.at) >= new Date());
  const past = events.filter((e) => new Date(e.at) < new Date());
  const Row = ({ e }) => (
    <li className="flex items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white p-4" data-testid={`calendar-event-${e.id}`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${e.kind === "visit" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>
          {e.kind === "visit" ? <Home className="h-5 w-5" /> : <Stethoscope className="h-5 w-5" />}
        </span>
        <div><p className="text-sm font-semibold text-stone-900">{e.title}</p><p className="text-xs text-stone-500">{fmtDate(e.at)}{e.location ? ` · ${e.location}` : ""}</p></div>
      </div>
      <StatusPill status={e.status} />
    </li>
  );
  return (
    <div data-testid="calendar-page">
      <PageHeader title="Care Calendar" sub="ParentOne visits, doctor appointments, diagnostics and reminders in one place."
        actions={<button onClick={() => setShowForm(!showForm)} className="p1-btn-primary !px-5 !py-2.5" data-testid="add-appointment-btn"><Plus className="h-4 w-4" /> Add Appointment</button>} />
      {showForm && (
        <form onSubmit={submit} className="p1-card mb-6 grid gap-4 p-5 sm:grid-cols-2" data-testid="appointment-form">
          {parents.length > 1 && (
            <div className="sm:col-span-2"><label className="p1-label">For whom?</label>
              <select className="p1-input" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="apt-parent">
                <option value="">— Select parent —</option>
                {parents.map((p) => <option key={p.id} value={p.id}>{p.label ? `${p.label} — ` : ""}{p.name}</option>)}
              </select></div>
          )}
          <div><label className="p1-label">Title *</label><input className="p1-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Dr. Joshi — Cardiology" data-testid="apt-title" /></div>
          <div><label className="p1-label">Date & time *</label><input type="datetime-local" className="p1-input" required value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} data-testid="apt-when" /></div>
          <div><label className="p1-label">Location</label><input className="p1-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="apt-location" /></div>
          <div><label className="p1-label">Notes</label><input className="p1-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="apt-notes" /></div>
          <div className="sm:col-span-2"><button className="p1-btn-primary !px-5 !py-2.5" data-testid="apt-submit">Save Appointment</button></div>
        </form>
      )}
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><CalendarDays className="h-5 w-5 text-emerald-800" /> Upcoming</h2>
      <ul className="space-y-3">{upcoming.length ? upcoming.map((e) => <Row key={e.id} e={e} />) : <p className="text-sm text-stone-500">Nothing upcoming.</p>}</ul>
      {past.length > 0 && (<>
        <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-stone-500">Past</h2>
        <ul className="space-y-3 opacity-70">{past.slice(-5).map((e) => <Row key={e.id} e={e} />)}</ul>
      </>)}
    </div>
  );
}
